import {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Textbox from "./activityComponents/Textbox";
import TextArea from "./activityComponents/TextArea";
import Checkboxes from "./activityComponents/Checkboxes";
import MatrixGauge, {
  normalizeGaugeValue,
  genPointId,
  inferQuadrant,
  getQuadrantLabelFromContent,
} from "./activityComponents/MatrixGauge";
import TwoDGauge from "./activityComponents/TwoDGauge";
import Timer from "./activityComponents/Timer";
import ActivitySlider from "./activityComponents/ActivitySlider";
import SendEmail from "./activityComponents/SendEmail";
import BuyService from "./activityComponents/BuyService";
import DataEvent from "./activityComponents/DataEvent";
import DataEventAnalysis from "./activityComponents/DataEventAnalysis";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import {
  Lightbulb,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  GripHorizontal, BookOpen,
  Globe,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import LoggerContext from "../context/LoggerProvider";
import {
  convertOldCustomToNew,
  getDirForLanguage,
  LANGUAGES,
  useLinkNestedDiagram,
} from "../lib/utils";
import {
  computeBackJumpNavigateToField,
  computeForwardJumpNavigateToField,
} from "../lib/navigateFieldUserData";
import AIPrompt from "./activityComponents/AIPrompt";
import "./RenderDialog.css";
import { config } from "../Environment";
import useRetryFetch from "../hooks/useRetryFetch";
import { toast } from "sonner";
import { t } from "i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useBranding } from "../branding/useBranding";
import { TiptapProvider } from "../context/TipTapProvider";
import TipTap from "../components/richtext/TipTap";
import ThoughtOrganizer from "../components/activityComponents/ThoughtOrganizer";
import TextMarkup from "../components/activityComponents/TextMarkup";
import SmartList from "../components/activityComponents/SmartList";
import SmartTable from "../components/activityComponents/SmartTable";
import ImageExtract from "../components/activityComponents/ImageExtract";
import MentalModelStudio from "../components/activityComponents/MentalModelStudio";
import Timestamp from "./activityComponents/Timestamp";
import ElapsedTime from "./activityComponents/ElapsedTime";
import { ReactComponent as ClaryNextLogo } from "../images/Logo.svg";
import CategoriesBoard from "./activityComponents/CategoriesBoard";
import PersonalDiagram from "./activityComponents/PersonalDiagram";
import { useTranslation } from "react-i18next";
import { FieldActionMenu } from "./ui/field-action-menu";
import { useQuery } from "@tanstack/react-query";
import { listTranslationKeys } from "../api/translationKeys";
import { resolveTranslationTokens } from "../lib/resolveTranslationTokens";
import { resolveHtmlTranslationTokens } from "../lib/resolveHtmlTranslationTokens";
import {
  formatFieldValue as formatFieldValueShared,
  findFieldOptions as findFieldOptionsShared,
} from "../lib/formatFieldValue";
import { ActivityNotesPopover } from "./runActivity/ActivityNotesPopover";
import SaveExampleDialog from "./dialogs/SaveExampleDialog";
import SubmitExampleConsentDialog from "./dialogs/SubmitExampleConsentDialog";
import ExampleManagement from "./examples/ExampleManagement";
import DecorationButton from "./DecorationButton";
import useAuth from "../hooks/useAuth";
import { RecordingPlayer } from "./recording/RecordingPlayer";
import { RecordingDialog } from "./recording/RecordingDialog";
import { AutoPlayMedia } from "./recording/AutoPlayMedia";
import { MediaNotePlayer } from "./recording/MediaNotePlayer";
import { deleteRecording } from "../api/recordings";
import { ImageViewer } from "./images/ImageViewer";
import { ImageUploadDialog } from "./images/ImageUploadDialog";
import { deleteImage } from "../api/processImages";
import { Mic, Video, Image, ImagePlus } from "lucide-react";
import { useAutoPlay } from "../hooks/useAutoPlay";
import {
  getTempPreviewData,
  saveTempPreviewData,
  clearTempPreviewData,
  saveExampleToBackend,
  submitExampleForApproval,
  fetchExamplesFromBackend,
} from "../lib/exampleStorage";
import { normalizeDataEventAnalysisItems } from "../lib/dataEventAnalysisViz";
import { paneHasInvalidCheckboxMin } from "../lib/checkboxConstraints";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

// Helper to extract note text from notes structure (handles both string and object formats)
const getNoteText = (noteData) => {
  if (!noteData) return "";
  if (typeof noteData === "string") return noteData;
  if (typeof noteData === "object" && noteData.text !== undefined)
    return noteData.text;
  return "";
};

// Helper to extract recordings from notes structure
const getNoteRecordings = (noteData) => {
  if (!noteData) return null;
  if (typeof noteData === "object" && noteData.recordings)
    return noteData.recordings;
  return null;
};

/** Deep-clone activity node so we never mutate diagram.data in place; avoids stale React state. */
function cloneActivityNodeForRun(raw) {
  if (!raw || typeof raw !== "object") return raw;
  try {
    if (typeof structuredClone === "function") {
      return structuredClone(raw);
    }
  } catch {
    /* fall through */
  }
  try {
    return JSON.parse(JSON.stringify(raw));
  } catch {
    return { ...raw };
  }
}

const RenderDialog = ({
  diagram,
  initialActivity,
  onDone,
  /** When set, terminal option saves RUNNING and lands on report pane instead of FINISHED (same+resume+landing). */
  onSoftExit = async (..._args) => {},
  softExitEnabled = false,
  onPause,
  onNextActivity,
  onNestedActivity,
  onBackFromNested,
  onBackToFinishedNested,
  initialStepStack,
  userData,
  setUserData,
  processId,
  setDiagram,
  onQuotePane,
  onNavigateToField,
  onAddTakeaway,
  vendorMetadata,
  onFollowExample,
  // Optional props for external prologue/epilogue control (e.g., from Review page edit mode)
  prologue: externalPrologue = undefined,
  epilogue: externalEpilogue = undefined,
  onPrologueChange: externalOnPrologueChange = undefined,
  onEpilogueChange: externalOnEpilogueChange = undefined,
  isEditMode = false,
  // Scroll callback for auto-hide header
  onContentScroll = undefined,
  // Mobile actions slot (takeaways, chat, help, menu, lang) rendered by parent
  mobileActionsSlot = null,
  // Auto-play props (optional)
  additionalInfoRecordings = undefined,
  autoPlayState: externalAutoPlayState = undefined,
  onAutoPlayStateChange = undefined,
  // When true (e.g. preview in design panel), use compact layout for components like Impact Gauge
  compactLayout = false,
}) => {
  const [currentActivity, setCurrentActivity] = useState("");
  const [currentActivityData, setCurrentActivityData] = useState({});

  // Which language to resolve this activity's translated content in —
  // distinct from i18n.language (the platform/account UI language, which
  // stays as the viewer's own language for buttons/chrome around the
  // activity). null means "use the activity's own authored language"
  // (diagram.prescriptionLanguage) — set explicitly once the viewer picks a
  // language from the switcher in the header below.
  const [contentLanguage, setContentLanguage] = useState(null);

  const [stepStack, setStepStack] = useState([]); // in order to allow to go back in "step history"
  const [redoStack, setRedoStack] = useState([]); // in order to allow to go forward after going back
  const { branding } = useBranding();

  const [nestedActivityIntent, setNestedActivityIntent] = useState({});
  const [finishedNextActivity, setFinishedNextActivity] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // For soft-exit: show loading on the clicked final button only.
  const [savingOptionIdx, setSavingOptionIdx] = useState(null);
  const [dataEventLogNonce, setDataEventLogNonce] = useState(0);
  const [paneValidationAttempt, setPaneValidationAttempt] = useState(0);


  const { Logger } = useContext(LoggerContext);

  const [quickCreating, setQuickCreating] = useState(false);

  const retryFetch = useRetryFetch();
  const linkNestedDiagram = useLinkNestedDiagram();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();

  const { data: translationKeys } = useQuery({
    queryKey: ["translation-keys"],
    queryFn: () => listTranslationKeys(retryFetch),
  });
  const translationsByKey = useMemo(
    () =>
      Object.fromEntries(
        (translationKeys ?? []).map((k) => [k.key, k.translations]),
      ),
    [translationKeys],
  );

  useEffect(() => {
    setPaneValidationAttempt(0);
  }, [currentActivity]);

  /** Every pane's `custom` items — Data Event Analysis must resolve labels for fields logged on other activities. */
  const allWorkflowCustomComponents = useMemo(() => {
    if (!diagram?.data || typeof diagram.data !== "object") return [];
    const out = [];
    for (const node of Object.values(diagram.data)) {
      const custom = node?.custom;
      if (!Array.isArray(custom)) continue;
      for (const comp of custom) {
        if (comp && typeof comp === "object") out.push(comp);
      }
    }
    return out;
  }, [diagram]);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const contentRef = useRef(null);
  const lastScrollTop = useRef(0);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const footerScrollRef = useRef(null);
  const scrollbarTrackRef = useRef(null);
  const buttonsDragRef = useRef({ startX: 0, startScroll: 0, dragged: false });
  const [footerScroll, setFooterScroll] = useState({
    canScroll: false,
    thumbLeft: 0,
    thumbWidth: 100,
  });
  const [isStandaloneBrowser, setIsStandaloneBrowser] = useState(false);
  console.log(stepStack, "stepStack");

  // Example management state
  const [showSaveExampleDialog, setShowSaveExampleDialog] = useState(false);
  const [showSubmitConsentDialog, setShowSubmitConsentDialog] = useState(false);
  const [showExamplesManagement, setShowExamplesManagement] = useState(false);
  const [pendingOnDoneParams, setPendingOnDoneParams] = useState(null);
  const [isMarkedAsExample, setIsMarkedAsExample] = useState(false);
  const [isActivityAuthor, setIsActivityAuthor] = useState(false);
  const [hasExamples, setHasExamples] = useState(false);

  // User recording dialog state
  const [userRecordingDialogOpen, setUserRecordingDialogOpen] = useState(false);
  const [userRecordingDialogType, setUserRecordingDialogType] =
    useState("audio");
  const [userRecordingTargetKey, setUserRecordingTargetKey] = useState("");
  const [checkingAuthor, setCheckingAuthor] = useState(true);

  // User image upload dialog state
  const [userImageUploadDialogOpen, setUserImageUploadDialogOpen] =
    useState(false);
  const [userImageTargetKey, setUserImageTargetKey] = useState("");

  // Notes state - keyed by activity ID
  const [paneNotes, setPaneNotes] = useState({});

  // Track which media notes have been auto-played
  const [autoPlayedMediaNotes, setAutoPlayedMediaNotes] = useState(new Set());

  // Internal prologue and epilogue state (for process mode and preview mode)
  const [internalPrologue, setInternalPrologue] = useState("");
  const [internalEpilogue, setInternalEpilogue] = useState("");

  // Use external values if provided (for edit mode in Review page), otherwise use internal state
  const prologue =
    externalPrologue !== undefined ? externalPrologue : internalPrologue;
  const epilogue =
    externalEpilogue !== undefined ? externalEpilogue : internalEpilogue;

  // Use external setter if provided, otherwise use internal setter
  const setPrologue = externalOnPrologueChange || setInternalPrologue;
  const setEpilogue = externalOnEpilogueChange || setInternalEpilogue;

  // Auto-play hook for activity recordings
  const currentActivityRecordings = additionalInfoRecordings?.[currentActivity];
  const {
    shouldAutoPlay,
    isPlaying: isAutoPlaying,
    setPlaying: setAutoPlaying,
    markAsPlayed,
    stopAll: stopAutoPlay,
  } = useAutoPlay({
    activityId: currentActivity,
    recordings: currentActivityRecordings,
    autoPlayState: externalAutoPlayState,
    processId,
    isDesignMode: isEditMode,
    onAutoPlayStateChange,
  });

  // Get the prescription/activity ID from searchParams
  const activityId = searchParams.get("wid") || searchParams.get("id");

  // Fetch process details to get current isExample state
  useEffect(() => {
    const fetchProcessExample = async () => {
      if (!processId) {
        setIsMarkedAsExample(false);
        return;
      }

      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/processes/${processId}`,
          { credentials: "include" },
        );

        if (response.ok) {
          const data = await response.json();
          setIsMarkedAsExample(data.process?.isExample || false);
        }
      } catch (error) {
        console.error("Failed to fetch process example state:", error);
      }
    };

    fetchProcessExample();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  // Check if examples exist for this activity
  useEffect(() => {
    if (!activityId) return;
    fetchExamplesFromBackend(activityId, retryFetch)
      .then((examples) => setHasExamples(examples?.length > 0))
      .catch(() => setHasExamples(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  // Scroll focused inputs into view on mobile (when keyboard opens)
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleFocusIn = (e) => {
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable
      ) {
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
      }
    };

    container.addEventListener("focusin", handleFocusIn);
    return () => container.removeEventListener("focusin", handleFocusIn);
  }, []);

  // Detect if running in standalone browser (not webview/PWA) and track viewport height
  const [viewportHeight, setViewportHeight] = useState("100vh");
  useEffect(() => {
    const isWebview =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      /wv|WebView/i.test(navigator.userAgent);
    setIsStandaloneBrowser(!isWebview);

    // Use dynamic viewport height for standalone browsers (accounts for address bar)
    const updateHeight = () => {
      if (!isWebview) {
        // window.innerHeight accounts for the actual visible area
        setViewportHeight(`${window.innerHeight}px`);
      } else {
        setViewportHeight("100vh");
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    // Also update on orientation change and visual viewport changes
    window.visualViewport?.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
      window.visualViewport?.removeEventListener("resize", updateHeight);
    };
  }, []);

  // Track horizontal scroll state of footer buttons for custom scrollbar
  useEffect(() => {
    const el = footerScrollRef.current;
    if (!el) return;

    const updateScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const canScroll = scrollWidth > clientWidth + 2;
      const thumbWidth = canScroll
        ? Math.max((clientWidth / scrollWidth) * 100, 20)
        : 100;
      const maxScroll = scrollWidth - clientWidth;
      const thumbLeft =
        maxScroll > 0 ? (scrollLeft / maxScroll) * (100 - thumbWidth) : 0;

      // Update thumb position via DOM to avoid React re-renders during scroll
      const track = scrollbarTrackRef.current;
      if (track) {
        const thumb = track.firstElementChild;
        if (thumb) {
          thumb.style.width = `${thumbWidth}%`;
          thumb.style.marginLeft = `${thumbLeft}%`;
        }
        track.parentElement.style.display = canScroll ? "" : "none";
      }

      // Only trigger React re-render when canScroll changes (not on every scroll frame)
      setFooterScroll((prev) => {
        if (prev.canScroll !== canScroll || prev.thumbWidth !== thumbWidth) {
          return { canScroll, thumbLeft, thumbWidth };
        }
        return prev;
      });
    };

    updateScroll();
    el.addEventListener("scroll", updateScroll, { passive: true });
    const resizeObserver = new ResizeObserver(updateScroll);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScroll);
      resizeObserver.disconnect();
    };
  }, [currentActivityData?.options]);

  // Drag handler for the custom scrollbar track
  const handleScrollbarPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      const track = scrollbarTrackRef.current;
      const el = footerScrollRef.current;
      if (!track || !el) return;

      const trackRect = track.getBoundingClientRect();
      const { scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) return;

      const thumbRatio = footerScroll.thumbWidth / 100;
      const setScrollFromX = (clientX) => {
        const clickRatio = (clientX - trackRect.left) / trackRect.width;
        const centered = clickRatio - thumbRatio / 2;
        const clamped = Math.max(0, Math.min(1 - thumbRatio, centered));
        el.scrollLeft = (clamped / (1 - thumbRatio)) * maxScroll;
      };

      setScrollFromX(e.clientX);

      const onMove = (ev) => {
        ev.preventDefault();
        setScrollFromX(ev.clientX);
      };
      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [footerScroll.thumbWidth],
  );

  // Drag-to-scroll handler for the buttons container (swipe support)
  const handleButtonsPointerDown = useCallback((e) => {
    const el = footerScrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth + 2) return;

    buttonsDragRef.current = {
      startX: e.clientX,
      startScroll: el.scrollLeft,
      dragged: false,
    };

    const onMove = (ev) => {
      const dx = buttonsDragRef.current.startX - ev.clientX;
      if (Math.abs(dx) > 3) {
        buttonsDragRef.current.dragged = true;
        el.scrollLeft = buttonsDragRef.current.startScroll + dx;
      }
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      setTimeout(() => {
        buttonsDragRef.current.dragged = false;
      }, 0);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }, []);

  // Check if current user is the activity author (same logic as Review.tsx)
  useEffect(() => {
    const checkIfAuthor = async () => {
      setCheckingAuthor(true);
      // Get prescription ID from diagram or activityId
      const prescriptionId = diagram?.fiPrescription || activityId;

      if (!prescriptionId || !currentUser) {
        setIsActivityAuthor(false);
        setCheckingAuthor(false);
        return;
      }

      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/prescriptions/${prescriptionId}`,
        );

        if (response.ok) {
          const prescriptionData = await response.json();

          // fiUser could be either an ID string or a populated user object
          const authorId =
            typeof prescriptionData.fiUser === "string"
              ? prescriptionData.fiUser
              : prescriptionData.fiUser?._id;

          // Check if current user is the author by comparing IDs
          const currentUserId = currentUser._id || currentUser.id;
          const isAuthor = String(currentUserId) === String(authorId);

          console.log("[RenderDialog] Author check result:", {
            currentUserId,
            authorId,
            isAuthor,
            prescriptionId,
          });

          setIsActivityAuthor(isAuthor);
        }
      } catch (error) {
        Logger.error(
          "[RenderDialog] Failed to check if user is author:",
          error,
        );
        setIsActivityAuthor(false);
      } finally {
        setCheckingAuthor(false);
      }
    };

    checkIfAuthor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagram?.fiPrescription, activityId, currentUser]);

  const handleOptionClick = async (optIdx) => {
    Logger.debug("===== handleOptionClick START =====");
    Logger.debug("handleOptionClick - optIdx:", optIdx);
    Logger.debug("handleOptionClick - currentActivity:", currentActivity);
    Logger.debug(
      "handleOptionClick - stepStack LENGTH before push:",
      stepStack.length,
    );
    Logger.debug("handleOptionClick - userData keys:", Object.keys(userData));
    Logger.debug(
      "handleOptionClick - userData FULL:",
      JSON.stringify(userData),
    );

    let currentOption = currentActivityData?.options?.[optIdx];
    if (!currentOption) {
      Logger.warn("handleOptionClick - missing option at index", optIdx);
      return;
    }

    if (
      !isEditMode &&
      paneHasInvalidCheckboxMin(currentActivityData?.custom, userData)
    ) {
      setPaneValidationAttempt((n) => n + 1);
      return;
    }

    let nextAct = currentOption.nextActivities[0];
    Logger.debug("handleOptionClick - nextAct:", nextAct);

    // Clear redoStack when committing to a new path (branching)
    if (redoStack.length > 0) {
      Logger.debug(
        "handleOptionClick - Clearing redoStack (user committed to new path)",
      );
      setRedoStack([]);
    }

    const isSoftExitPath =
      !nextAct &&
      processId &&
      softExitEnabled &&
      typeof onSoftExit === "function";

    // push currentActivity to stack
    let newStepStack = [...stepStack];
    const newEntry = {
      activity: currentActivity,
      userData, // Save current screen's userData (accumulated from all previous + current)
    };
    Logger.debug(
      "handleOptionClick - pushing entry:",
      JSON.stringify(newEntry, null, 2),
    );
    newStepStack.push(newEntry);

    Logger.debug(
      "handleOptionClick - newStepStack LENGTH after push:",
      newStepStack.length,
    );
    Logger.debug(
      "handleOptionClick - newStepStack FULL:",
      JSON.stringify(newStepStack, null, 2),
    );
    // For soft-exit, avoid local stepStack update to prevent visible pane refresh
    // right before redirect to dashboard.
    if (!isSoftExitPath) {
      setStepStack(newStepStack);
    }

    if (nextAct) {
      Logger.debug("handleOptionClick - Navigating to next activity:", nextAct);

      // Preserve user-entered values when revisiting panes in loops.
      // Do not clear pane fields on re-entry; users expect prior inputs to remain.

      // jump to next activity
      setCurrentActivity(nextAct);
      // NOTE: Don't reset currentActivityData to {} - let useEffect update it to avoid blank screen

      setFinishedNextActivity(false);
      await onNextActivity(currentOption.value, nextAct, newStepStack);
      setFinishedNextActivity(true);
      Logger.debug("===== handleOptionClick END (next activity) =====");

      // Is next activity a prescription?
    } else {
      Logger.debug(
        "handleOptionClick - No next activity, checking for temp data to save as example",
      );

      // In preview mode (no processId), always show save example dialog
      // Even single-pane activities without user data can be saved as examples
      if (activityId && !processId) {
        // Preview mode: Show save example dialog
        setPendingOnDoneParams({
          optionValue: currentOption.value,
          userData,
          stepStack: newStepStack,
        });
        setShowSaveExampleDialog(true);
        Logger.debug(
          "===== handleOptionClick - Preview mode: Showing save example dialog =====",
        );
      } else {
        // Process mode: soft exit (looping engagement) or full completion
        if (isSoftExitPath) {
          setSavingOptionIdx(optIdx);
          try {
            await onSoftExit(currentOption.value, userData, newStepStack);
          } finally {
            setSavingOptionIdx(null);
          }
        } else {
          setIsSaving(true);
          try {
            await onDone(currentOption.value, userData, newStepStack);
          } finally {
            setIsSaving(false);
          }
        }
        Logger.debug("===== handleOptionClick END (done) =====");
      }
    }
  };

  const handleCloseClick = () => {
    // Just navigate - auto-save will handle saving on unmount
    onPause();
  };

  // Example handlers
  const handleSaveExample = useCallback(
    async (exampleInfo) => {
      if (!activityId || !pendingOnDoneParams) return;

      try {
        // Save the example to backend as a process with isExample flag
        const exampleData = {
          userData: pendingOnDoneParams.userData,
          notes: paneNotes,
        };

        // The backend POST /api/processes/:presId expects prescription ID
        // and will automatically handle prescription history
        await saveExampleToBackend(
          activityId, // prescriptionId
          exampleData, // { userData, notes }
          exampleInfo.name, // example name
          exampleInfo.description, // example description
          pendingOnDoneParams.stepStack, // navigation history
          currentActivity, // nextActivity (current position)
          activityId, // prescriptionHistoryId (will be handled by backend)
          prologue, // prologue text (from state)
          epilogue, // epilogue text (from state)
          retryFetch,
        );

        // Clear temp data from localStorage
        clearTempPreviewData(activityId);

        // Show success message
        toast.success(t("example_saved_successfully"));

        // Now proceed with onDone
        await onDone(
          pendingOnDoneParams.optionValue,
          pendingOnDoneParams.userData,
          pendingOnDoneParams.stepStack,
        );

        setPendingOnDoneParams(null);
      } catch (error) {
        console.error("Failed to save example:", error);
        toast.error(t("failed_to_save_example"));
      }
    },
    [
      activityId,
      pendingOnDoneParams,
      onDone,
      t,
      paneNotes,
      retryFetch,
      currentActivity,
      prologue,
      epilogue,
    ],
  );

  const handleDiscardExample = useCallback(async () => {
    if (!activityId || !pendingOnDoneParams) return;

    try {
      // Clear temp data
      clearTempPreviewData(activityId);

      // Proceed with onDone without saving
      await onDone(
        pendingOnDoneParams.optionValue,
        pendingOnDoneParams.userData,
        pendingOnDoneParams.stepStack,
      );

      setPendingOnDoneParams(null);
    } catch (error) {
      console.error("Error during discard:", error);
    }
  }, [activityId, pendingOnDoneParams, onDone]);

  const handleLoadExample = useCallback(
    (example) => {
      if (example.data) {
        // Handle new structure with userData and notes
        if (example.data.userData) {
          setUserData(example.data.userData);
        } else {
          // Backward compatibility: if no userData key, treat entire data as userData
          setUserData(example.data);
        }

        if (example.data.notes) {
          setPaneNotes(example.data.notes);
        }
        toast.success(t("example_loaded_successfully"));
      }
    },
    [setUserData, t],
  );

  // Handler for opening submit consent dialog (regular users)
  const handleSubmitForApproval = useCallback(() => {
    setShowSubmitConsentDialog(true);
  }, []);

  // Handler for submitting example for approval (after consent)
  const handleSubmitExampleConfirm = useCallback(
    async (isAnonymous) => {
      if (!processId) {
        toast.error(t("process_id_required") || "Process ID is required");
        return;
      }

      try {
        // Get example name from SaveExampleDialog or use default
        const exampleName = prompt(
          t("enter_example_name") || "Enter a name for this example:",
          t("example_default_name") || "My Example",
        );

        if (!exampleName) {
          toast.error(t("example_name_required") || "Example name is required");
          return;
        }

        const exampleDescription = prompt(
          t("enter_example_description") || "Enter a description (optional):",
          "",
        );

        await submitExampleForApproval(
          processId,
          exampleName,
          exampleDescription || "",
          prologue,
          epilogue,
          paneNotes,
          isAnonymous,
          retryFetch,
        );

        toast.success(
          t("example_submitted_for_review") ||
            "Your example has been submitted for review",
        );
        setShowSubmitConsentDialog(false);
      } catch (error) {
        console.error("Failed to submit example:", error);
        toast.error(
          error.message ||
            t("failed_to_submit_example") ||
            "Failed to submit example",
        );
      }
    },
    [processId, prologue, epilogue, paneNotes, retryFetch, t],
  );

  // Handler for updating notes for current activity (saves to backend via API)
  const handleNotesChange = useCallback(
    async (value, _immediate = false) => {
      if (!currentActivity) return;

      // Get existing note data to preserve recordings
      const existingNoteData = paneNotes[currentActivity];
      const existingRecordings =
        typeof existingNoteData === "object" && existingNoteData?.recordings
          ? existingNoteData.recordings
          : null;

      // Create note value that preserves recordings structure
      const noteValue = existingRecordings
        ? { text: value, recordings: existingRecordings }
        : value; // Keep as string if no recordings exist

      const updatedNotes = {
        ...paneNotes,
        [currentActivity]: noteValue,
      };

      setPaneNotes(updatedNotes);

      // Only save to backend if we have a processId (process mode, not preview mode)
      if (processId) {
        try {
          await retryFetch(
            `${config.API_BASE}/api/processes/${processId}/notes`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ notes: updatedNotes }),
            },
          );
          Logger.debug("[RenderDialog] Notes saved successfully");
        } catch (error) {
          Logger.error("[RenderDialog] Failed to save notes:", error);
          toast.error(t("failed_to_save_notes") || "Failed to save notes");
        }
      }
    },
    [currentActivity, paneNotes, processId, retryFetch, Logger, t],
  );

  // Handler for updating prologue (saves to backend via API)
  const handlePrologueChange = useCallback(
    async (value) => {
      // Always update local state (needed for preview mode)
      setPrologue(value);

      // Only save to backend if we have a processId (not in preview mode)
      if (!processId) return;

      try {
        await retryFetch(
          `${config.API_BASE}/api/processes/${processId}/prologue`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ prologue: value }),
          },
        );
        Logger.debug("[RenderDialog] Prologue saved successfully");
      } catch (error) {
        Logger.error("[RenderDialog] Failed to save prologue:", error);
        toast.error(t("failed_to_save_prologue") || "Failed to save prologue");
      }
    },
    [processId, retryFetch, Logger, t],
  );

  // Handler for updating epilogue (saves to backend via API)
  const handleEpilogueChange = useCallback(
    async (value) => {
      // Always update local state (needed for preview mode)
      setEpilogue(value);

      // Only save to backend if we have a processId (not in preview mode)
      if (!processId) return;

      try {
        await retryFetch(
          `${config.API_BASE}/api/processes/${processId}/epilogue`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ epilogue: value }),
          },
        );
        Logger.debug("[RenderDialog] Epilogue saved successfully");
      } catch (error) {
        Logger.error("[RenderDialog] Failed to save epilogue:", error);
        toast.error(t("failed_to_save_epilogue") || "Failed to save epilogue");
      }
    },
    [processId, retryFetch, Logger, t],
  );

  // Handler for updating pane recordings (updates local state to keep in sync with backend)
  // Note: The recording is already saved to backend via the RecordingDialog,
  // this handler just keeps the frontend state in sync
  const handlePaneRecordingsChange = useCallback(
    (recordings) => {
      if (!currentActivity) return;

      // Get existing note data
      const existingNoteData = paneNotes[currentActivity];
      const existingText =
        typeof existingNoteData === "object" && existingNoteData?.text
          ? existingNoteData.text
          : typeof existingNoteData === "string"
            ? existingNoteData
            : "";

      // Create note value with updated recordings
      const noteValue = {
        text: existingText,
        recordings: recordings || {},
      };

      const updatedNotes = {
        ...paneNotes,
        [currentActivity]: noteValue,
      };

      setPaneNotes(updatedNotes);
      Logger.debug("[RenderDialog] Pane recordings updated in local state");
    },
    [currentActivity, paneNotes, Logger],
  );


  // Handler for updating annotation display mode — uses dedicated endpoint that also patches PrescriptionHistory
  const handleAnnotationDisplayModeChange = useCallback(
    async (mode) => {
      if (!currentActivity || !diagram?.data) return;

      // Optimistically update local diagram state so the selector reflects the change immediately
      const updatedDiagram = {
        ...diagram,
        data: {
          ...diagram.data,
          [currentActivity]: { ...diagram.data[currentActivity], annotationDisplayMode: mode },
        },
      };
      if (setDiagram) setDiagram(updatedDiagram);

      const prescriptionId = diagram?.fiPrescription || activityId;
      if (prescriptionId) {
        try {
          await retryFetch(
            `${config.API_BASE}/api/prescriptions/${prescriptionId}/annotation-mode`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ activityId: currentActivity, mode }),
            },
          );
          Logger.debug("[RenderDialog] Annotation display mode saved:", mode);
        } catch (err) {
          Logger.error("[RenderDialog] Failed to save annotation display mode:", err);
        }
      }
    },
    [currentActivity, diagram, activityId, setDiagram, retryFetch, Logger],
  );

  const handleBackClick = async () => {
    Logger.debug("===== handleBackClick START =====");
    Logger.debug("handleBackClick - stepStack LENGTH:", stepStack.length);
    Logger.debug(
      "handleBackClick - stepStack FULL:",
      JSON.stringify(stepStack, null, 2),
    );
    Logger.debug("handleBackClick - currentActivity:", currentActivity);
    Logger.debug(
      "handleBackClick - current userData keys:",
      Object.keys(userData),
    );

    if (stepStack.length) {
      let newStack = [...stepStack];
      let prevAct = newStack.pop();

      // Save current activity to redoStack for forward navigation
      const currentEntry = {
        activity: currentActivity,
        userData, // Save current screen's userData
      };
      Logger.debug(
        "handleBackClick - pushing to redoStack:",
        JSON.stringify(currentEntry, null, 2),
      );
      setRedoStack((prev) => [...prev, currentEntry]);

      Logger.debug(
        "handleBackClick - popped entry:",
        JSON.stringify(prevAct, null, 2),
      );
      Logger.debug(
        "handleBackClick - newStack LENGTH after pop:",
        newStack.length,
      );
      Logger.debug(
        "handleBackClick - newStack FULL after pop:",
        JSON.stringify(newStack, null, 2),
      );

      let theActivity =
        typeof prevAct === "string" ? prevAct : prevAct.activity;

      if (theActivity === "TERMINATE") {
        // going back from nested to outer prescription
        // will terminate the inner one
        Logger.debug("handleBackClick - TERMINATE case");
        await onBackFromNested();
      } else if (theActivity.includes("REOPEN")) {
        // stepping back from outer to (already finished) nested prescription
        // Reopen and use latest step from it
        let nestedProcessId = theActivity.split("|")[1];
        Logger.debug(
          "REOPEN detected for nested process id: " + nestedProcessId,
        );
        await onBackToFinishedNested(nestedProcessId, newStack);
      } else {
        Logger.debug("handleBackClick - NORMAL back case");
        // update stack
        setStepStack(newStack);

        // jump to previous activity
        setCurrentActivity(theActivity);
        // NOTE: Don't reset currentActivityData to {} - let useEffect update it to avoid blank screen

        // IMPORTANT: Accumulate userData from ALL previous steps up to this point
        // This ensures we have all data collected in previous screens.
        // Start from the current userData (not {}) so fields belonging to screens
        // ahead of where we're going (e.g. the screen we're leaving) are preserved -
        // otherwise navigating back would permanently drop that data from userData,
        // and a subsequent "Next" (not redo/forward) would save without it.
        Logger.debug(
          "handleBackClick - Building accumulatedUserData from newStack...",
        );
        let accumulatedUserData = { ...userData };
        for (let i = 0; i < newStack.length; i++) {
          const step = newStack[i];
          Logger.debug(
            `  newStack[${i}]: activity="${
              step.activity
            }", userData keys=[${Object.keys(step.userData ?? {}).join(", ")}]`,
          );
          const stepUserData = step.userData ?? {};
          Object.entries(stepUserData).forEach(([key, value]) => {
            accumulatedUserData[key] = value;
          });
        }
        // Add the current step's userData (the one we're navigating TO)
        Logger.debug("handleBackClick - Adding prevAct userData...");
        Logger.debug(
          `  prevAct: activity="${
            prevAct.activity
          }", userData keys=[${Object.keys(prevAct.userData ?? {}).join(", ")}]`,
        );
        if (prevAct.userData) {
          Object.entries(prevAct.userData).forEach(([key, value]) => {
            accumulatedUserData[key] = value;
          });
        }

        Logger.debug(
          "handleBackClick - Final accumulatedUserData keys:",
          Object.keys(accumulatedUserData),
        );
        Logger.debug(
          "handleBackClick - Final accumulatedUserData FULL:",
          JSON.stringify(accumulatedUserData),
        );
        Logger.debug("===== handleBackClick END =====");

        // IMPORTANT: Await the backend update to ensure consistency during rapid navigation
        // Pass accumulatedUserData explicitly since setUserData below hasn't applied yet
        await onNextActivity("GOINGBACK", theActivity, newStack, accumulatedUserData);
        setUserData(accumulatedUserData);
      }
    } else {
      Logger.debug("handleBackClick - stepStack is EMPTY, cannot go back");
    }
  };

  const handleForwardClick = async () => {
    Logger.debug("===== handleForwardClick START =====");
    Logger.debug("handleForwardClick - redoStack LENGTH:", redoStack.length);
    Logger.debug(
      "handleForwardClick - redoStack FULL:",
      JSON.stringify(redoStack, null, 2),
    );
    Logger.debug("handleForwardClick - currentActivity:", currentActivity);

    if (redoStack.length) {
      let newRedoStack = [...redoStack];
      let nextAct = newRedoStack.pop();

      Logger.debug(
        "handleForwardClick - popped from redoStack:",
        JSON.stringify(nextAct, null, 2),
      );
      Logger.debug(
        "handleForwardClick - newRedoStack LENGTH after pop:",
        newRedoStack.length,
      );

      // Push current activity back onto stepStack
      const currentEntry = {
        activity: currentActivity,
        userData, // Save current userData
      };
      Logger.debug(
        "handleForwardClick - pushing to stepStack:",
        JSON.stringify(currentEntry, null, 2),
      );
      let newStepStack = [...stepStack, currentEntry];

      setStepStack(newStepStack);
      setRedoStack(newRedoStack);

      let theActivity =
        typeof nextAct === "string" ? nextAct : nextAct.activity;

      // Navigate to next activity
      setCurrentActivity(theActivity);
      // NOTE: Don't reset currentActivityData to {} - let useEffect update it to avoid blank screen

      // Restore userData from the forward step (includes accumulated data),
      // merged on top of current userData so edits made on the screen we're
      // leaving (and any other fields not part of nextAct's snapshot) aren't lost.
      Logger.debug(
        "handleForwardClick - Restoring userData from nextAct:",
        JSON.stringify(nextAct.userData ?? {}),
      );
      const restoredUserData = { ...userData, ...(nextAct.userData ?? {}) };
      setUserData(restoredUserData);

      // Update backend with forward navigation (using "GOINGFORWARD" as action)
      // Pass restoredUserData explicitly since setUserData above hasn't applied yet
      await onNextActivity("GOINGFORWARD", theActivity, newStepStack, restoredUserData);

      Logger.debug("===== handleForwardClick END =====");
    } else {
      Logger.debug(
        "handleForwardClick - redoStack is EMPTY, cannot go forward",
      );
    }
  };

  /** Strip keys from live userData and every stepStack/redoStack snapshot (loop-safe reset after Data Event). */
  const clearUserDataKeysFromSession = useCallback((keys, snapshot = null) => {
    if (!keys?.length) return;
    const keySet = new Set(keys);
    const isSame = (a, b) => {
      if (a === b) return true;
      const ta = typeof a;
      const tb = typeof b;
      if (ta !== tb) return false;
      if (a == null || b == null) return false;
      if (ta === "object") {
        try {
          return JSON.stringify(a) === JSON.stringify(b);
        } catch {
          return false;
        }
      }
      return false;
    };
    const strip = (ud) => {
      if (!ud || typeof ud !== "object") return ud;
      const next = { ...ud };
      keySet.forEach((k) => {
        if (
          snapshot &&
          Object.prototype.hasOwnProperty.call(snapshot, k) &&
          !isSame(next[k], snapshot[k])
        ) {
          // user changed this key after the log snapshot; don't wipe it
          return;
        }
        delete next[k];
      });
      return next;
    };
    setUserData((prev) => strip(prev));
    setStepStack((prev) =>
      prev.map((step) => {
        if (typeof step === "string") return step;
        return { ...step, userData: strip(step.userData) };
      }),
    );
    setRedoStack((prev) =>
      prev.map((step) => {
        if (typeof step === "string") return step;
        return { ...step, userData: strip(step.userData) };
      }),
    );
  }, []);

  const handleStringValueChange = (key, value) => {
    setUserData((oldUserData) => {
      let newUserData = { ...oldUserData };
      newUserData[key] = value;
      return newUserData;
    });
  };

  const handleOptionValueChange = (key, value) => {
    setUserData((oldUserData) => {
      let newUserData = { ...oldUserData };
      newUserData[key] = value;
      // Sync: when a linked list updates, remove gauge points whose list item was removed
      const components = currentActivityData?.custom;
      if (
        components &&
        value &&
        typeof value === "object" &&
        Array.isArray(value.items)
      ) {
        const listItemIds = new Set((value.items || []).map((i) => i.id));
        components
          .filter(
            (c) =>
              c.type === "gauge" &&
              (c.content?.linkedListKeys || []).includes(key),
          )
          .forEach((c) => {
            const gaugeKey = c.content.key;
            const current = oldUserData[gaugeKey];
            const normalized = normalizeGaugeValue(current);
            const nextPoints = (normalized.points || []).filter(
              (p) =>
                !(
                  p.sourceListKey === key &&
                  p.listItemId !== undefined &&
                  !listItemIds.has(p.listItemId)
                ),
            );
            newUserData[gaugeKey] = { points: nextPoints };
          });
      }
      return newUserData;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const lastGaugeDropPositionRef = useRef({ gaugeKey: null, x: 150, y: 150 });
  const [lastAddedGaugePoint, setLastAddedGaugePoint] = useState(null);

  const handleGaugeListDragEnd = (event) => {
    const { active, over } = event;
    if (active?.data?.current?.type !== "LIST_ITEM" || !over?.id) return;
    const gaugeId = String(over.id);
    if (!gaugeId.startsWith("gauge-")) return;
    const gaugeKey = gaugeId.slice(6);
    const { sourceListKey, listItemId, text } = active.data.current;
    const pos =
      lastGaugeDropPositionRef.current?.gaugeKey === gaugeKey
        ? lastGaugeDropPositionRef.current
        : null;
    const dropX = pos ? Math.max(0, Math.min(300, Math.round(pos.x))) : 150;
    const dropY = pos ? Math.max(0, Math.min(300, Math.round(pos.y))) : 150;
    const components = currentActivityData?.custom;
    const gaugeItem = Array.isArray(components)
      ? components.find(
          (c) => c.type === "gauge" && c.content?.key === gaugeKey,
        )
      : null;
    const gaugeContent = gaugeItem?.content || {};
    const { quadrant } = inferQuadrant(dropX, dropY);
    const quadrantLabel = getQuadrantLabelFromContent(quadrant, gaugeContent);
    const newPointId = genPointId();
    setUserData((oldUserData) => {
      const current = oldUserData[gaugeKey];
      const normalized = normalizeGaugeValue(current);
      const alreadyPlaced = normalized.points.some(
        (p) => p.sourceListKey === sourceListKey && p.listItemId === listItemId,
      );
      if (alreadyPlaced) return oldUserData;
      const newPoint = {
        id: newPointId,
        x: dropX,
        y: dropY,
        quadrant,
        quadrantLabel,
        label: text || "",
        listItemId,
        sourceListKey,
      };
      const nextPoints = [...normalized.points, newPoint];
      return { ...oldUserData, [gaugeKey]: { points: nextPoints } };
    });
    setLastAddedGaugePoint({ gaugeKey, pointId: newPointId });
  };

  // Open recording dialog for user input
  const openUserRecordingDialog = (contentKey, componentType) => {
    setUserRecordingTargetKey(contentKey);
    setUserRecordingDialogType(
      componentType === "audio_input" ? "audio" : "video",
    );
    setUserRecordingDialogOpen(true);
  };

  // Handle user recording completion
  const handleUserRecordingComplete = (recordingData) => {
    handleStringValueChange(userRecordingTargetKey, recordingData);
    setUserRecordingDialogOpen(false);
  };

  // Handle user recording deletion
  const handleDeleteUserRecording = async (contentKey) => {
    const recording = userData[contentKey];
    if (recording?.key) {
      try {
        await deleteRecording(
          processId,
          recording.mimeType?.includes("audio") ? "audio" : "video",
          "pane",
          contentKey,
          retryFetch,
        );
      } catch (error) {
        console.error("Failed to delete recording:", error);
      }
    }
    handleStringValueChange(contentKey, null);
  };

  // Open user image upload dialog
  const openUserImageUploadDialog = (contentKey) => {
    setUserImageTargetKey(contentKey);
    setUserImageUploadDialogOpen(true);
  };

  // Handle user image upload completion
  const handleUserImageUploadComplete = (imageData) => {
    handleStringValueChange(userImageTargetKey, imageData);
    setUserImageUploadDialogOpen(false);
  };

  // Handle user image deletion
  const handleDeleteUserImage = async (contentKey) => {
    const image = userData[contentKey];
    if (image?.key) {
      try {
        await deleteImage(processId, contentKey, retryFetch);
      } catch (error) {
        console.error("Failed to delete image:", error);
      }
    }
    handleStringValueChange(contentKey, null);
  };

  // Collect all values for a field key across stepStack iterations
  const aggregateFieldValues = (fieldKey) => {
    const values = [];
    for (const step of stepStack) {
      if (
        step.userData &&
        step.userData[fieldKey] !== undefined &&
        step.userData[fieldKey] !== null &&
        step.userData[fieldKey] !== ""
      ) {
        values.push(step.userData[fieldKey]);
      }
    }
    // Also include current userData value if present
    if (
      userData[fieldKey] !== undefined &&
      userData[fieldKey] !== null &&
      userData[fieldKey] !== ""
    ) {
      values.push(userData[fieldKey]);
    }
    return values;
  };

  const formatFieldValue = (fieldValue, fieldOptions) =>
    formatFieldValueShared(fieldValue, fieldOptions);

  const findFieldOptions = (fieldKey) =>
    findFieldOptionsShared(diagram?.data, fieldKey);

  const interpolate = (content) => {
    let result = content;
    if (result.prompt) {
      result = result.prompt;
    }
    let matches = result.match(/\{.*?\}/g);

    if (!matches) return content;

    for (let match of matches) {
      let variableName = match.match(/\{(.*)\}/)[1];
      let transformed;

      if (variableName.endsWith("[]")) {
        // Aggregate mode: collect all values from stepStack + current userData
        const baseKey = variableName.slice(0, -2);
        const allValues = aggregateFieldValues(baseKey);
        const fieldOptions = findFieldOptions(baseKey);
        transformed = allValues
          .map((v) => {
            const formatted = formatFieldValue(v, fieldOptions);
            // Wrap each iteration in brackets for clarity
            return "[" + formatted + "]";
          })
          .join(", ");
      } else {
        // Default: latest value from userData
        transformed = structuredClone(userData[variableName]);
        if (transformed === undefined || transformed === null) {
          transformed = "";
        } else if (typeof transformed !== "string") {
          const fieldOptions = findFieldOptions(variableName);
          transformed = formatFieldValue(transformed, fieldOptions);
        }
      }

      let newPart = "<b>" + (transformed || "") + "</b>";
      result = result.replace(match, newPart);
    }
    return result;
  };

  // Helper function to quote a specific field in chat
  const handleQuoteField = (fieldLabel, fieldKey) => {
    if (onQuotePane && currentActivity) {
      const quoteData = {
        screen: currentActivity,
        screenLabel:
          currentActivityData?.label?.[diagram.prescriptionLanguage] ||
          currentActivity,
        component: fieldLabel,
        componentKey: fieldKey,
      };

      console.log("[RenderDialog] Quoting field:", quoteData);
      onQuotePane(quoteData);
    } else {
      console.warn(
        "[RenderDialog] Cannot quote field - onQuotePane or currentActivity missing",
        {
          hasOnQuotePane: !!onQuotePane,
          currentActivity,
        },
      );
    }
  };

  // Helper function to copy field content as an Action Item
  const handleCopyToAction = (content, label) => {
    if (onAddTakeaway && content.trim()) {
      console.log("[RenderDialog] Copying to Action Item:", label, content);
      onAddTakeaway("action", content, label);
    } else {
      console.warn(
        "[RenderDialog] Cannot copy to action - onAddTakeaway missing or content empty",
      );
    }
  };

  // Helper function to copy field content as an Insight
  const handleCopyToInsight = (content, label) => {
    if (onAddTakeaway && content.trim()) {
      console.log("[RenderDialog] Copying to Insight:", label, content);
      onAddTakeaway("insight", content, label);
    } else {
      console.warn(
        "[RenderDialog] Cannot copy to insight - onAddTakeaway missing or content empty",
      );
    }
  };

  // Helper function to navigate to a referenced field (called when clicking message references)
  const handleNavigateToField = async (reference) => {
    Logger.debug("[RenderDialog] handleNavigateToField called:", reference);
    Logger.debug(
      "[RenderDialog] Current stepStack:",
      stepStack.map((s) => (typeof s === "string" ? s : s.activity)),
    );
    Logger.debug(
      "[RenderDialog] Current redoStack:",
      redoStack.map((s) => (typeof s === "string" ? s : s.activity)),
    );
    Logger.debug("[RenderDialog] Current activity:", currentActivity);

    if (!reference || !reference.screen) {
      Logger.warn("[RenderDialog] Invalid reference:", reference);
      return;
    }

    // Check if we're already on the referenced screen
    if (reference.screen === currentActivity) {
      Logger.debug(
        "[RenderDialog] Already on the referenced screen, just highlighting field",
      );
      // Just scroll and highlight the field
      if (reference.componentKey) {
        setTimeout(() => {
          const element = document.querySelector(
            `[data-field-id="${reference.componentKey}"]`,
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.classList.add("ring-2", "ring-primary", "ring-offset-2");
            setTimeout(() => {
              element.classList.remove(
                "ring-2",
                "ring-primary",
                "ring-offset-2",
              );
            }, 2000);
          } else {
            Logger.warn(
              `[RenderDialog] Could not find element with data-field-id="${reference.componentKey}"`,
            );
          }
        }, 100);
      }
      return;
    }

    // Check if the referenced screen is in our stepStack (a previous step - go BACK)
    const stepIndex = stepStack.findIndex((step) => {
      const activity = typeof step === "string" ? step : step.activity;
      return activity === reference.screen;
    });

    // Check if the referenced screen is in our redoStack (a future step - go FORWARD)
    const redoIndex = redoStack.findIndex((step) => {
      const activity = typeof step === "string" ? step : step.activity;
      return activity === reference.screen;
    });

    if (stepIndex !== -1) {
      // Found in stepStack - navigate backward
      Logger.debug(
        `[RenderDialog] Found referenced screen in stepStack at index ${stepIndex}`,
      );
      Logger.debug(
        `[RenderDialog] Current stepStack length: ${stepStack.length}`,
      );

      const stepsToGoBack = stepStack.length - stepIndex;
      Logger.debug(`[RenderDialog] Need to go back ${stepsToGoBack} steps`);

      const backJump = computeBackJumpNavigateToField({
        stepStack,
        redoStack,
        currentActivity,
        userData,
        referenceScreen: reference.screen,
      });

      if (!backJump) {
        Logger.warn(
          `[RenderDialog] Failed to compute back jump to "${reference.screen}"`,
        );
      } else {
        const { targetActivity, newStepStack, newRedoStack, accumulatedUserData } =
          backJump;

        Logger.debug(`[RenderDialog] Navigating to: ${targetActivity}`);
        Logger.debug(
          `[RenderDialog] New stepStack length: ${newStepStack.length}`,
        );
        Logger.debug(
          `[RenderDialog] New redoStack length: ${newRedoStack.length}`,
        );

        setStepStack(newStepStack);
        setRedoStack(newRedoStack);
        setCurrentActivity(targetActivity);
        setUserData(accumulatedUserData);

        // Pass accumulatedUserData explicitly since setUserData above hasn't applied yet
        await onNextActivity("GOINGBACK", targetActivity, newStepStack, accumulatedUserData);
      }

      // After navigation, scroll to and highlight the field
      if (reference.componentKey) {
        setTimeout(() => {
          const element = document.querySelector(
            `[data-field-id="${reference.componentKey}"]`,
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.classList.add("ring-2", "ring-primary", "ring-offset-2");
            setTimeout(() => {
              element.classList.remove(
                "ring-2",
                "ring-primary",
                "ring-offset-2",
              );
            }, 2000);
          } else {
            Logger.warn(
              `[RenderDialog] Could not find element with data-field-id="${reference.componentKey}"`,
            );
          }
        }, 300); // Longer delay after navigation to ensure rendering
      }
    } else if (redoIndex !== -1) {
      // Found in redoStack - navigate forward
      Logger.debug(
        `[RenderDialog] Found referenced screen in redoStack at index ${redoIndex}`,
      );
      Logger.debug(
        `[RenderDialog] Current redoStack length: ${redoStack.length}`,
      );

      const stepsToGoForward = redoStack.length - redoIndex;
      Logger.debug(
        `[RenderDialog] Need to go forward ${stepsToGoForward} steps`,
      );

      const forwardJump = computeForwardJumpNavigateToField({
        stepStack,
        redoStack,
        currentActivity,
        userData,
        referenceScreen: reference.screen,
      });

      if (!forwardJump) {
        Logger.warn(
          `[RenderDialog] Failed to compute forward jump to "${reference.screen}"`,
        );
      } else {
        const { targetActivity, newStepStack, newRedoStack, targetUserData } =
          forwardJump;

        Logger.debug(`[RenderDialog] Navigating to: ${targetActivity}`);
        Logger.debug(
          `[RenderDialog] New stepStack length: ${newStepStack.length}`,
        );
        Logger.debug(
          `[RenderDialog] New redoStack length: ${newRedoStack.length}`,
        );

        setStepStack(newStepStack);
        setRedoStack(newRedoStack);
        setCurrentActivity(targetActivity);
        setUserData(targetUserData);

        // Pass targetUserData explicitly since setUserData above hasn't applied yet
        await onNextActivity("GOINGFORWARD", targetActivity, newStepStack, targetUserData);
      }

      // After navigation, scroll to and highlight the field
      if (reference.componentKey) {
        setTimeout(() => {
          const element = document.querySelector(
            `[data-field-id="${reference.componentKey}"]`,
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.classList.add("ring-2", "ring-primary", "ring-offset-2");
            setTimeout(() => {
              element.classList.remove(
                "ring-2",
                "ring-primary",
                "ring-offset-2",
              );
            }, 2000);
          } else {
            Logger.warn(
              `[RenderDialog] Could not find element with data-field-id="${reference.componentKey}"`,
            );
          }
        }, 300); // Longer delay after navigation to ensure rendering
      }
    } else {
      Logger.warn(
        `[RenderDialog] Referenced screen "${reference.screen}" not found in stepStack or redoStack`,
      );
      Logger.debug(
        `[RenderDialog] Current stepStack:`,
        stepStack.map((s) => (typeof s === "string" ? s : s.activity)),
      );
      Logger.debug(
        `[RenderDialog] Current redoStack:`,
        redoStack.map((s) => (typeof s === "string" ? s : s.activity)),
      );
      // The referenced step might not yet be visited or is on a different branch
    }
  };

  const handleCreateQuickActivity = async (quickactivityComp) => {
    setQuickCreating(true);
    try {
      // Collect current user's values for those fields
      const relevantFieldKeys =
        quickactivityComp.content.relevantFieldKeys || [];
      const fieldsToSend = relevantFieldKeys
        .reverse()
        .reduce((acc, fieldKey) => {
          let label = "";

          // Search through all activity nodes to find the matching label
          for (const node of Object.values(diagram.data)) {
            if (!node.custom) continue;
            for (const block of node.custom) {
              if (
                block.content &&
                typeof block.content === "object" &&
                block.content.key === fieldKey
              ) {
                label = block.content.label;
                break;
              }
            }

            if (label) break; // stop once label is found
          }
          const value = userData[fieldKey];
          acc[label || fieldKey] = value; // fallback to fieldKey if label not found
          return acc;
        }, {});
      const response = await retryFetch(
        `${config.API_BASE}/api/ai/create-from-suggestion`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            userAnswers: fieldsToSend,
            stepsLimit: 3,
            settings: quickactivityComp.content?.settings,
          }),
        },
      );

      const data = await response.json();
      if (data.error) throw data.error;
      else {
        const newActivityName = data?.savedActivity?.name;
        const newActivityId = data?.savedActivity?._id;
        const newActivityDiagram = data?.savedActivity?.diagramJSON;
        const currentPrescriptionId = searchParams.get("wid"); // wid is the prescriptionId
        toast.success(
          t("activity_created_successfully", { name: newActivityName }),
        );
        setQuickCreating("success");

        // link the new activity

        if (
          quickactivityComp.content.createNested &&
          setDiagram &&
          newActivityId
        ) {
          try {
            const updatedDiagram = await linkNestedDiagram({
              diagram: diagram,
              fromNodeId: currentActivity,
              nestedPrescId: newActivityId,
              nestedLabel: newActivityName,
              nestedDiagram: newActivityDiagram,
              addReverseEdge: quickactivityComp.content?.linkBack,
            });
            // update prescription
            let updatePrescription = await retryFetch(
              `${config.API_BASE}/api/prescriptions/${currentPrescriptionId}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                  diagram: updatedDiagram,
                  valid: true,
                }),
              },
            );
            if (updatePrescription.ok) {
              let updateProcess = await retryFetch(
                `${config.API_BASE}/api/processes/${processId}/diagram`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    diagramJSON: updatedDiagram,
                  }),
                },
              );
              if (updateProcess.ok) {
                setDiagram(updatedDiagram);
              }
            }
          } catch (err) {
            console.error(
              "Error linking nested activity and updating the prescription and process:",
              err,
            );
          }
        }
      }
    } catch (err) {
      console.error("Error creating activity:", err);
      toast.error(t("error_creating_activity") || "Error creating activity");
      setQuickCreating(false);
    } finally {
    }
  };

  // Helper to get text alignment based on language direction
  const getTextAlign = () => {
    const dir = getDirForLanguage(contentLanguage || diagram.prescriptionLanguage) === "rtl" ? "rtl" : "ltr";
    return dir === "rtl" ? "right" : "left";
  };

  const renderItem = (item, allItems = []) => {
    switch (item.type) {
      // informative elements
      case "interpolation":
        return (
          <div
            className="p-3 mt-2 text-sm border rounded-xl bg-primary/5 border-primary/15 text-gray-800 leading-relaxed"
            style={{ textAlign: getTextAlign() }}
            dangerouslySetInnerHTML={{ __html: interpolate(item.content) }}
          ></div>
        );
      case "aiprompt":
        return (
          <AIPrompt
            prompt={interpolate(item.content)}
            componentKey={item.id}
            setUserData={setUserData}
            settings={item.settings}
          />
        );
      case "personal_diagram":
        return (
          <PersonalDiagram
            content={item.content}
            userData={userData}
            diagramData={diagram?.data}
          />
        );
      case "richtext":
      case "richtextnew":
        return (
          <div
            className="mt-2 text-sm rounded-lg ProseMirror"
            dir={getDirForLanguage(contentLanguage || diagram.prescriptionLanguage) === "rtl" ? "rtl" : "ltr"}
            dangerouslySetInnerHTML={{ __html: item.content }}
          ></div>
        );
      case "richtext_dynamic":
        return (
          <div className="mt-2 ">
            <TiptapProvider>
              <TipTap
                hideMenu={!item.content.showTextEditor}
                defaultValue={userData[item.id] || item.content?.data}
                onUpdate={(val) => handleStringValueChange(item.id, val)}
              ></TipTap>
            </TiptapProvider>
          </div>
        );
      case "hr":
        return (
          <div className="h-px my-3 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        );
      case "headline1":
        return (
          <h2 className="my-2 text-xl font-bold leading-tight text-gray-900 text-start">
            {item.content}
          </h2>
        );
      case "headline2":
        return (
          <h3 className="pl-2 my-2 text-base font-semibold leading-tight text-gray-800 border-l-2 border-primary text-start">
            {item.content}
          </h3>
        );
      case "paragraph":
        return (
          <p className="my-1 text-sm leading-relaxed text-gray-700 text-start">
            {item.content}
          </p>
        );
      case "orderedlist":
        return (
          <div className="p-3 my-2 border rounded-xl shadow-sm optioncomponent text-start bg-white border-gray-200">
            <Label className="block mb-1 text-sm font-semibold text-gray-800">
              {item.content.label}
            </Label>
            <ol
              className="space-y-1 text-sm"
              style={{ listStyleType: "decimal", marginLeft: "16px" }}
            >
              {item.content.items.map((olitem) => (
                <li
                  key={olitem.key}
                  className="pl-1 leading-relaxed text-gray-700"
                >
                  {olitem.label}
                </li>
              ))}
            </ol>
          </div>
        );
      // interactive elements
      case "textbox":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={userData[item.content.key] || ""}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onAddTakeaway
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onAddTakeaway
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            disabled={!onQuotePane && !onAddTakeaway}
          >
            <div data-field-id={item.content.key}>
              <Textbox
                itemkey={item.id}
                content={item.content}
                defaultValue={
                  userData[item.content.key] ||
                  diagram.flowcontext?.find((entry) => entry.key === item.id)
                    ?.defaultValue ||
                  ""
                }
                onChange={handleStringValueChange}
              />
            </div>
          </FieldActionMenu>
        );
      case "textarea":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={userData[item.content.key] || ""}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onAddTakeaway
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onAddTakeaway
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            disabled={!onQuotePane && !onAddTakeaway}
          >
            <div data-field-id={item.content.key}>
              <TextArea
                itemkey={item.id}
                content={item.content}
                defaultValue={
                  userData[item.content.key] ||
                  diagram.flowcontext?.find((entry) => entry.key === item.id)
                    ?.defaultValue ||
                  ""
                }
                onChange={handleStringValueChange}
              />
            </div>
          </FieldActionMenu>
        );
      case "checkbox":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={formatFieldValue(
              userData[item.content.key] || "",
              item.content.options,
            )}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onAddTakeaway
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onAddTakeaway
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            disabled={!onQuotePane && !onAddTakeaway}
          >
            <div data-field-id={item.content.key}>
              <Checkboxes
                itemkey={item.id}
                content={item.content}
                defaultValue={userData[item.content.key] || ""}
                value={userData[item.content.key]}
                onChange={handleOptionValueChange}
                validationAttempt={paneValidationAttempt}
              />
            </div>
          </FieldActionMenu>
        );
      case "gauge":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={formatFieldValue(
              userData[item.content.key] || {},
              item.content.options,
            )}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onAddTakeaway
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onAddTakeaway
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            disabled={!onQuotePane && !onAddTakeaway}
          >
            <div
              data-field-id={item.content.key}
              className="min-w-0 overflow-hidden max-w-full"
            >
              <MatrixGauge
                itemkey={item.id}
                content={item.content}
                defaultValue={userData[item.content.key] || {}}
                value={userData[item.content.key]}
                onChange={handleOptionValueChange}
                compact={compactLayout}
                connectedListKey={(item.content.linkedListKeys || [])[0]}
                connectedListItems={(() => {
                  const key = (item.content.linkedListKeys || [])[0];
                  if (!key) return undefined;
                  const raw = userData[key];
                  return Array.isArray(raw?.items) ? raw.items : undefined;
                })()}
                onDragOverPosition={(gaugeKey, x, y) => {
                  lastGaugeDropPositionRef.current = { gaugeKey, x, y };
                }}
                selectPointIdWhenAdded={
                  lastAddedGaugePoint?.gaugeKey === item.content.key
                    ? lastAddedGaugePoint.pointId
                    : null
                }
                onDidSelectPoint={() => setLastAddedGaugePoint(null)}
              />
            </div>
          </FieldActionMenu>
        );
      case "twodgauge":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={formatFieldValue(
              userData[item.content.key] || {},
              item.content.options,
            )}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onAddTakeaway
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onAddTakeaway
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            disabled={!onQuotePane && !onAddTakeaway}
          >
            <div data-field-id={item.content.key}>
              <TwoDGauge
                itemkey={item.id}
                content={item.content}
                defaultValue={userData[item.content.key] || {}}
                onChange={handleOptionValueChange}
                label={item.content.label}
                xAxisLabel={item.content.xAxisLabel || "Internal"}
                yAxisLabel={item.content.yAxisLabel || "External"}
                enableFeedback={item.content.enableFeedback !== false}
                lowProgressZone={
                  item.content.lowProgressZone || {
                    x: 20,
                    y: 20,
                    operator: "AND",
                  }
                }
                highProgressZone={
                  item.content.highProgressZone || {
                    x: 75,
                    y: 75,
                    operator: "OR",
                  }
                }
                lowProgressMessage={item.content.lowProgressMessage}
                highProgressMessage={item.content.highProgressMessage}
                placeholderMessage={item.content.placeholderMessage}
              />
            </div>
          </FieldActionMenu>
        );
      case "timer":
        return (
          <Timer
            itemkey={item.id}
            content={item.content}
            defaultValue={userData[item.content.key] || "00:00"}
            value={userData[item.content.key]}
            mode={item.content.mode}
            countdownValue={item.content.countdownValue}
            alarmSound={
              item.content.alarmSound === true
                ? "bell"
                : item.content.alarmSound === false || item.content.alarmSound == null
                  ? null
                  : item.content.alarmSound
            }
            allowDynamicTime={item.content.allowDynamicTime}
            dynamicTimeLabel={item.content.dynamicTimeLabel}
            onChange={handleStringValueChange}
          />
        );
      case "slider":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={String(userData[item.content.key] || "")}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onAddTakeaway
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onAddTakeaway
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            disabled={!onQuotePane && !onAddTakeaway}
          >
            <div data-field-id={item.content.key}>
              <ActivitySlider
                itemkey={item.id}
                content={item.content}
                defaultValue={userData[item.content.key]}
                value={userData[item.content.key]}
                onChange={handleStringValueChange}
              />
            </div>
          </FieldActionMenu>
        );
      case "send_email":
        return (
          <SendEmail
            processId={processId}
            userData={userData}
            itemkey={item.id}
            content={item.content}
            defaultValue={userData[item.content.key] || ""}
            onChange={handleStringValueChange}
          />
        );
      case "data_event":
        return (
          <DataEvent
            processId={processId}
            activity={currentActivity}
            userData={userData}
            itemkey={item.id}
            content={item.content}
            onChange={handleStringValueChange}
            onResetFieldKeys={clearUserDataKeysFromSession}
            onLogged={() => setDataEventLogNonce((n) => n + 1)}
          />
        );
      case "data_event_analysis":
        return (
          <DataEventAnalysis
            processId={processId}
            activity={currentActivity}
            content={item.content}
            activityComponents={allWorkflowCustomComponents}
            refreshNonce={dataEventLogNonce}
          />
        );
      case "timestamp":
        return (
          <Timestamp
            itemkey={item.id}
            content={item.content}
            defaultValue={userData[item.content.key]}
            onChange={handleStringValueChange}
          />
        );
      case "elapsed_time":
        return (
          <ElapsedTime
            itemkey={item.id}
            content={item.content}
            startValue={userData[item.content.start_ref]}
            stopValue={userData[item.content.stop_ref]}
            defaultValue={userData[item.content.key]}
            onChange={handleStringValueChange}
          />
        );
      case "buy_service":
        return (
          <BuyService
            processId={processId}
            currentActivity={currentActivity}
            itemkey={item.id}
            content={item.content}
            userData={userData[item.content.key] || {}}
            onChange={handleOptionValueChange}
          />
        );
      case "quick_activity":
        // Render button for end user to create a quick activity
        return (
          item.content.relevantFieldKeys?.length > 0 && (
            <div className="flex flex-col items-center my-4" key={item.id}>
              <Button
                variant="shine"
                onClick={() => handleCreateQuickActivity(item)}
                disabled={quickCreating}
              >
                {quickCreating === "success" ? (
                  <FontAwesomeIcon
                    icon={solid("check-circle")}
                    className="mr-2 text-green-500"
                  />
                ) : quickCreating ? (
                  <FontAwesomeIcon
                    icon={solid("spinner")}
                    spin
                    className="mr-2"
                  />
                ) : (
                  <FontAwesomeIcon icon={solid("bolt")} className="mr-2" />
                )}
                {quickCreating === "success"
                  ? "Activity Created"
                  : "Create Quick Activity"}
              </Button>
              <div className="text-xs italic text-gray-500">
                <FontAwesomeIcon icon={solid("flask")} className="me-1" />
                {t("experimental_feature_ai_authoring")}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {quickCreating === "success" && !item.content.createNested
                  ? t("quick_activity_created_successfully")
                  : ""}
              </div>
            </div>
          )
        );
      case "thoughts_organizer":
        return (
          <ThoughtOrganizer
            config={{
              ...item.content.config,
              initialItems: userData[item.content.key]?.items || [],
            }}
            userData={userData}
            content={item.content}
            initialText={structuredClone(userData[item.content.inputFieldKey])}
            onChange={handleStringValueChange}
          />
        );
      case "text_markup":
        return (
          <TextMarkup
            content={item.content}
            userData={userData[item.content.key]}
            initialValue={userData[item.content.key]}
            onChange={handleStringValueChange}
            readonly={false}
            dir={getDirForLanguage(contentLanguage || diagram.prescriptionLanguage) === "rtl" ? "rtl" : "ltr"}
          />
        );
      case "smart_list": {
        return (
          <SmartList
            config={{
              ...item.content.config,
              initialItems: userData[item.content.key]?.items || [],
            }}
            userData={userData}
            content={item.content}
            onChange={handleStringValueChange}
            linkedGaugeKeys={[]}
          />
        );
      }
      case "smart_table": {
        return (
          <SmartTable
            config={item.content.config}
            userData={userData}
            content={item.content}
            onChange={handleStringValueChange}
            lang={diagram?.prescriptionLanguage}
          />
        );
      }
      case "image_extract": {
        return (
          <ImageExtract
            config={item.content.config}
            userData={userData}
            content={item.content}
            onChange={handleStringValueChange}
            lang={diagram?.prescriptionLanguage}
            findTableColumns={(tableKey) => {
              for (const node of Object.values(diagram?.data ?? {})) {
                for (const comp of node?.custom ?? []) {
                  if (comp.content?.key === tableKey && comp.type === "smart_table") {
                    return comp.content?.config?.columns ?? [];
                  }
                }
              }
              return [];
            }}
          />
        );
      }
      case "categories_board":
        return (
          <CategoriesBoard
            config={{
              ...item.content.config,
            }}
            userData={userData}
            content={item.content}
            onChange={handleStringValueChange}
          />
        );
      case "mental_model_studio":
      case "mental_model_path":
        return (
          <MentalModelStudio
            mode={item.type === "mental_model_path" ? "guided" : "studio"}
            config={item.content.config}
            userData={userData}
            content={item.content}
            onChange={handleStringValueChange}
          />
        );
      case "audio_note":
      case "video_note":
        const mediaNoteType = item.type === "audio_note" ? "audio" : "video";
        const mediaNoteItemId = `${currentActivity}-${item.id}`;
        const shouldAutoPlayMediaNote =
          item.content?.autoPlay && !autoPlayedMediaNotes.has(mediaNoteItemId);

        return item.content?.recording ? (
          <div className="my-3">
            <MediaNotePlayer
              recording={item.content.recording}
              type={mediaNoteType}
              label={item.content.label}
              autoPlay={shouldAutoPlayMediaNote}
              itemId={mediaNoteItemId}
              onAutoPlayed={(id) =>
                setAutoPlayedMediaNotes((prev) => new Set([...prev, id]))
              }
              autoPlayedItems={autoPlayedMediaNotes}
            />
            {item.content.passive && (
              <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                <span>ℹ</span>
                <span>No response required — just watch/listen and continue.</span>
              </p>
            )}
          </div>
        ) : null;
      case "audio_input":
      case "video_input":
        const inputType = item.type === "audio_input" ? "audio" : "video";
        const existingRecording = userData[item.content.key];

        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={existingRecording ? `${inputType} recording` : ""}
            onQuote={
              onQuotePane
                ? () => handleQuoteField(item.content.label, item.content.key)
                : undefined
            }
          >
            <div data-field-id={item.content.key} className="space-y-3">
              {/* Label */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">
                  {item.content.label}
                </label>
              </div>

              {/* Recording UI */}
              <div className="p-4 border rounded-lg bg-muted/10">
                {existingRecording ? (
                  <div className="space-y-3">
                    <RecordingPlayer
                      recording={existingRecording}
                      type={inputType}
                      onDelete={() =>
                        handleDeleteUserRecording(item.content.key)
                      }
                      compact={true}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        openUserRecordingDialog(item.content.key, item.type)
                      }
                    >
                      {t("re_record")}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() =>
                      openUserRecordingDialog(item.content.key, item.type)
                    }
                    className="w-full"
                  >
                    {inputType === "audio" ? (
                      <Mic className="w-4 h-4 mr-2" />
                    ) : (
                      <Video className="w-4 h-4 mr-2" />
                    )}
                    {t("record_response")}
                  </Button>
                )}
              </div>
            </div>
          </FieldActionMenu>
        );
      case "image_note":
        return item.content?.image ? (
          <div className="p-3 my-3 border rounded-lg bg-muted/10">
            {item.content.label && (
              <p className="mb-2 text-sm font-medium text-muted-foreground">
                {item.content.label}
              </p>
            )}
            {item.content.hideThumbnail ? (
              <ImageViewer
                image={item.content.image}
                compact={true}
                readOnly={true}
                hideThumbnail={true}
              />
            ) : (
              <ImageViewer
                image={item.content.image}
                compact={true}
                readOnly={true}
              />
            )}
          </div>
        ) : null;
      case "image_input":
        const existingImage = userData[item.content.key];

        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={existingImage ? "image uploaded" : ""}
            onQuote={
              onQuotePane
                ? () => handleQuoteField(item.content.label, item.content.key)
                : undefined
            }
          >
            <div data-field-id={item.content.key} className="space-y-3">
              {/* Label */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">
                  {item.content.label}
                </label>
              </div>

              {/* Image Upload UI */}
              <div className="p-4 border rounded-lg bg-muted/10">
                {existingImage ? (
                  <div className="space-y-3">
                    <ImageViewer
                      image={existingImage}
                      onDelete={() => handleDeleteUserImage(item.content.key)}
                      compact={true}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        openUserImageUploadDialog(item.content.key)
                      }
                    >
                      {t("replace_image")}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => openUserImageUploadDialog(item.content.key)}
                    className="w-full"
                  >
                    <ImagePlus className="w-4 h-4 mr-2" />
                    {t("upload_image")}
                  </Button>
                )}
              </div>
            </div>
          </FieldActionMenu>
        );
      default:
    }
  };

  const renderActivityComponents = () => {
    console.log(
      `[RenderDialog] renderActivityComponents for currentActivity ${currentActivity} with data ${JSON.stringify(
        currentActivityData,
      )}`,
    );
    console.log(currentActivityData, "currentActivityData");
    let components = currentActivityData?.custom;

    // NEW: components must already be converted!!
    if (components) {
      if (Array.isArray(components)) {
        return (
          <DndContext sensors={sensors} onDragEnd={handleGaugeListDragEnd}>
            {components.map((item) => (
              <div
                className="mx-2 md:mx-5"
                key={
                  item.type === "data_event_analysis"
                    ? `${item.id}-${JSON.stringify(normalizeDataEventAnalysisItems(item.content))}`
                    : item.id
                }
              >
                {renderItem(item, components)}
              </div>
            ))}
          </DndContext>
        );
      } else {
        // old custom type found, should NEVER be the case
        Logger.error(
          "Old custom data in currentActivityData found, should have been converted in useMemo function already!",
        );
      }
    } else {
      return <></>;
    }

    // if (components) {
    //   if (Array.isArray(components)) {
    //     return (
    //       <>
    //         {components.map((item) => (
    //           <div key={item.id}>{renderItem(item)}</div>
    //         ))}
    //       </>
    //     );
    //   } else {
    //     // old custom type found
    //     components = convertOldCustomToNew(components);
    //     return (
    //       <>
    //         {components.map((item) => (
    //           <div key={item.id}>{renderItem(item)}</div>
    //         ))}
    //       </>
    //     );
    //   }
  };

  useEffect(() => {
    if (initialActivity) setIsLoading(false);
    Logger.debug(
      "RenderDialog with initialActivity and diagram data",
      initialActivity,
      diagram.nodes,
      diagram.edges,
    );
    setCurrentActivity(initialActivity);
    setCurrentActivityData({});
  }, [initialActivity]);

  useEffect(() => {
    Logger.debug("RenderDialog with initialStepStack", initialStepStack);
    setStepStack(initialStepStack);
  }, [initialStepStack]);

  // Load temp preview data when component mounts
  useEffect(() => {
    if (activityId && !processId) {
      // Only load temp data in preview mode (no processId)
      const tempData = getTempPreviewData(activityId);
      if (tempData) {
        if (tempData.userData && Object.keys(tempData.userData).length > 0) {
          Logger.debug(
            "Loading temp preview userData from localStorage:",
            tempData.userData,
          );
          setUserData(tempData.userData);
        }
        if (tempData.notes) {
          Logger.debug(
            "Loading temp preview notes from localStorage:",
            tempData.notes,
          );
          setPaneNotes(tempData.notes);
        }
        if (tempData.prologue) {
          Logger.debug(
            "Loading temp preview prologue from localStorage:",
            tempData.prologue,
          );
          setInternalPrologue(tempData.prologue);
        }
        if (tempData.epilogue) {
          Logger.debug(
            "Loading temp preview epilogue from localStorage:",
            tempData.epilogue,
          );
          setInternalEpilogue(tempData.epilogue);
        }
      }
    }
  }, [activityId, processId]); // Only run once on mount

  // Auto-save userData, notes, prologue, and epilogue to temp storage (debounced)
  useEffect(() => {
    if (!activityId || processId) return; // Only in preview mode

    const timeoutId = setTimeout(() => {
      const hasData = userData && Object.keys(userData).length > 0;
      const hasNotes = paneNotes && Object.keys(paneNotes).length > 0;
      const hasPrologue = prologue && prologue.trim().length > 0;
      const hasEpilogue = epilogue && epilogue.trim().length > 0;

      if (hasData || hasNotes || hasPrologue || hasEpilogue) {
        Logger.debug("Auto-saving temp preview data to localStorage");
        const tempData = {
          userData: userData || {},
          notes: paneNotes || {},
          prologue: prologue || "",
          epilogue: epilogue || "",
        };
        saveTempPreviewData(activityId, tempData);
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [userData, paneNotes, prologue, epilogue, activityId, processId]);

  // Load prologue and epilogue when in process mode
  useEffect(() => {
    if (processId) {
      const loadPrologueAndEpilogue = async () => {
        try {
          const response = await retryFetch(
            `${config.API_BASE}/api/processes/${processId}`,
            {
              method: "GET",
              credentials: "include",
            },
          );

          if (response.ok) {
            const processData = await response.json();
            setInternalPrologue(processData.prologue || "");
            setInternalEpilogue(processData.epilogue || "");
            Logger.debug("[RenderDialog] Prologue and epilogue loaded:", {
              prologue: processData.prologue,
              epilogue: processData.epilogue,
            });
          }
        } catch (error) {
          Logger.error(
            "[RenderDialog] Failed to load prologue and epilogue:",
            error,
          );
        }
      };

      loadPrologueAndEpilogue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  useEffect(() => {
    (async function () {
      Logger.debug(
        "Data from current activity will be detected",
        currentActivity,
        diagram.data,
        diagram.edges,
      );
      let theData = {};

      if (currentActivity && diagram.data && diagram.edges) {
        const rawActivity = diagram.data[currentActivity];
        if (rawActivity) {
          theData = cloneActivityNodeForRun(rawActivity);
          console.log(theData, "theData");
          if (theData.type !== "prescription" || !processId) {
            // find handle as sourceHandle in edges, if necessary
            for (let option of theData.options || []) {
              if (option.handle) {
                let nextActivities = [];

                for (let edge of diagram.edges) {
                  if (edge.sourceHandle === option.handle) {
                    nextActivities.push(edge.target);
                  }
                }
                delete option.handle; // remove handle, so no additional search necessary
                option.nextActivities = nextActivities;
              }
            }
          }
          if (theData.custom && !Array.isArray(theData.custom)) {
            theData.custom = convertOldCustomToNew(theData.custom);
          }

          if (Array.isArray(theData.custom)) {
            // Content language is distinct from i18n.language (the platform
            // UI language) — defaults to the activity's own authored
            // language so callers that don't pass contentLanguage resolve
            // exactly as before.
            const lang = contentLanguage || diagram.prescriptionLanguage || "en";
            const fallbackLang = diagram.prescriptionLanguage || "en";

            // Auto-captured fields are resolved by their hidden __tid
            // sibling property, not by scanning text for {{key}} patterns —
            // the field's own text IS the base-language value, so a field
            // with no __tid (never auto-captured yet) or no matching pool
            // row (translation not yet reviewed) simply keeps its stored
            // text as-is. resolveTranslationTokens/resolveHtmlTranslationTokens
            // (regex-based) still run afterward, for the narrow optional
            // "lock this term" overlay — an author-typed {{key}} inside
            // otherwise-plain text.
            const resolveByTid = (text, tid) => {
              if (typeof text !== "string" || !text.length) return text;
              const entry = tid ? translationsByKey?.[tid] : null;
              const resolved = entry?.[lang] || (fallbackLang !== lang ? entry?.[fallbackLang] : null) || text;
              return resolveTranslationTokens(resolved, translationsByKey, lang, fallbackLang).resolved;
            };
            const resolveHtmlByTid = (html, tid) => {
              if (typeof html !== "string" || !html.length) return html;
              const entry = tid ? translationsByKey?.[tid] : null;
              const resolved = entry?.[lang] || (fallbackLang !== lang ? entry?.[fallbackLang] : null) || html;
              return resolveHtmlTranslationTokens(resolved, translationsByKey, lang, fallbackLang);
            };

            for (const block of theData.custom) {
              if (!block) continue;

              if (block.type === "richtextnew" && typeof block.content === "string") {
                // richtextnew's content is a raw HTML string (Tiptap
                // getHTML()) — resolve via the HTML-safe resolver so only
                // text nodes are touched, never tag attributes.
                block.content = resolveHtmlByTid(block.content, block.__tid);
                continue;
              }

              if (
                (block.type === "headline1" ||
                  block.type === "headline2" ||
                  block.type === "paragraph") &&
                typeof block.content === "string"
              ) {
                // These 3 types' content is a plain string, not an object —
                // handled separately since the object-only checks below
                // would otherwise skip them entirely.
                block.content = resolveByTid(block.content, block.__tid);
                continue;
              }

              // aiprompt / interpolation: string template body (may include
              // {fieldKey} placeholders). Resolve language first; interpolate()
              // at render time still fills field refs from userData.
              if (
                (block.type === "aiprompt" || block.type === "interpolation") &&
                typeof block.content === "string"
              ) {
                block.content = resolveByTid(block.content, block.__tid);
                if (
                  block.type === "aiprompt" &&
                  block.settings &&
                  typeof block.settings === "object"
                ) {
                  for (const field of ["triggerButtonText", "preButtonMessage"]) {
                    if (typeof block.settings[field] === "string") {
                      block.settings[field] = resolveByTid(
                        block.settings[field],
                        block.settings[`${field}__tid`],
                      );
                    }
                  }
                }
                continue;
              }

              if (!block.content || typeof block.content !== "object")
                continue;

              if (block.type === "personal_diagram") {
                if (Array.isArray(block.content.nodes)) {
                  for (const pdNode of block.content.nodes) {
                    if (pdNode && typeof pdNode.authorText === "string") {
                      pdNode.authorText = resolveByTid(
                        pdNode.authorText,
                        pdNode.authorText__tid,
                      );
                    }
                  }
                }
                if (Array.isArray(block.content.edges)) {
                  for (const pdEdge of block.content.edges) {
                    if (pdEdge && typeof pdEdge.label === "string") {
                      pdEdge.label = resolveByTid(
                        pdEdge.label,
                        pdEdge.label__tid,
                      );
                    }
                  }
                }
                continue;
              }

              if (typeof block.content.label === "string") {
                block.content.label = resolveByTid(block.content.label, block.content.label__tid);
              }
              if (Array.isArray(block.content.options)) {
                for (const option of block.content.options) {
                  if (option && typeof option.label === "string") {
                    option.label = resolveByTid(option.label, option.label__tid);
                  }
                }
              }
              if (Array.isArray(block.content.items)) {
                for (const listItem of block.content.items) {
                  if (listItem && typeof listItem.label === "string") {
                    listItem.label = resolveByTid(listItem.label, listItem.label__tid);
                  }
                }
              }
              if (
                block.type === "timer" &&
                typeof block.content.dynamicTimeLabel === "string"
              ) {
                block.content.dynamicTimeLabel = resolveByTid(
                  block.content.dynamicTimeLabel,
                  block.content.dynamicTimeLabel__tid,
                );
              } else if (block.type === "twodgauge") {
                for (const field of [
                  "xAxisLabel",
                  "yAxisLabel",
                  "lowProgressMessage",
                  "highProgressMessage",
                  "placeholderMessage",
                ]) {
                  if (typeof block.content[field] === "string") {
                    block.content[field] = resolveByTid(block.content[field], block.content[`${field}__tid`]);
                  }
                }
              } else if (block.type === "gauge") {
                for (const field of [
                  "xAxisLabel",
                  "yAxisLabel",
                  "xLowLabel",
                  "xHighLabel",
                  "yLowLabel",
                  "yHighLabel",
                  "q1Label",
                  "q2Label",
                  "q3Label",
                  "q4Label",
                  "q1Prompt",
                  "q2Prompt",
                  "q3Prompt",
                  "q4Prompt",
                ]) {
                  if (typeof block.content[field] === "string") {
                    block.content[field] = resolveByTid(block.content[field], block.content[`${field}__tid`]);
                  }
                }
              } else if (
                block.type === "richtext_dynamic" &&
                typeof block.content.data === "string"
              ) {
                // richtext_dynamic's content is { data: "<html>", ... } —
                // data is the raw HTML string (Tiptap getHTML()), same
                // HTML-safe resolution as richtextnew above.
                block.content.data = resolveHtmlByTid(block.content.data, block.content.data__tid);
              } else if (
                block.type === "text_markup" &&
                typeof block.content.proposalText === "string"
              ) {
                // proposalText is a raw HTML string (MinimalEditor's Tiptap
                // instance, getHTML()) — same HTML-safe resolution as
                // richtextnew/richtext_dynamic above.
                block.content.proposalText = resolveHtmlByTid(
                  block.content.proposalText,
                  block.content.proposalText__tid,
                );
              }

              // Nested-config block types — translatable text lives inside
              // block.content.config, not flat on block.content.
              if (
                block.content.config &&
                typeof block.content.config === "object"
              ) {
                if (
                  block.type === "smart_list" ||
                  block.type === "categories_board" ||
                  block.type === "thoughts_organizer"
                ) {
                  for (const field of ["title", "placeholder"]) {
                    if (typeof block.content.config[field] === "string") {
                      block.content.config[field] = resolveByTid(
                        block.content.config[field],
                        block.content.config[`${field}__tid`],
                      );
                    }
                  }
                } else if (
                  block.type === "mental_model_studio" ||
                  block.type === "mental_model_path"
                ) {
                  if (typeof block.content.config.intro === "string") {
                    block.content.config.intro = resolveByTid(
                      block.content.config.intro,
                      block.content.config.intro__tid,
                    );
                  }
                }
              }
            }
          }

          Logger.debug("New current activity data", theData);

          if (theData.type === "prescription") {
            Logger.debug(
              "Inner nested prescription detected",
              theData.historyId,
              theData.options,
            );

            setNestedActivityIntent({
              historyId: theData.historyId,
              options: theData.options,
              currentActivity,
            });
          }
        }
      }

      setCurrentActivityData(theData);
    })();
  }, [diagram, currentActivity, processId, contentLanguage, translationsByKey]);

  useEffect(() => {
    (async function () {
      if (nestedActivityIntent.historyId && finishedNextActivity) {
        await onNestedActivity(
          nestedActivityIntent.historyId,
          nestedActivityIntent.options,
          nestedActivityIntent.currentActivity,
        );

        setFinishedNextActivity(false);
        setNestedActivityIntent({});
      }
    })();
  }, [finishedNextActivity, nestedActivityIntent]);

  // Expose handleNavigateToField to parent component
  useEffect(() => {
    if (onNavigateToField) {
      onNavigateToField(handleNavigateToField);
    }
  }, [onNavigateToField, handleNavigateToField]);

  return (
    <>
      {/* Auto-play media for activity recordings */}
      {currentActivityRecordings &&
        (shouldAutoPlay.audio || shouldAutoPlay.video) && (
          <AutoPlayMedia
            audio={currentActivityRecordings.audio}
            video={currentActivityRecordings.video}
            text={(() => {
              // Extract additional info text from current activity data
              const additionalInfo =
                currentActivityData?.metadata?.additionalInfo;
              if (!additionalInfo) return undefined;
              if (typeof additionalInfo === "string") return additionalInfo;
              if (typeof additionalInfo === "object" && additionalInfo.text)
                return additionalInfo.text;
              return undefined;
            })()}
            shouldAutoPlay={shouldAutoPlay}
            isPlaying={isAutoPlaying}
            onPlayingChange={setAutoPlaying}
            onEnded={(type) => {
              // Mark as played for single mode
              markAsPlayed(type);
            }}
            onClose={stopAutoPlay}
          />
        )}

      {currentActivityData?.type !== "prescription" || !processId ? (
        <div className="flex items-center justify-center h-full">
          <div
            className="relative bg-white flex flex-col md:h-fit md:max-h-[calc(100vh-6rem)] lg:max-h-[calc(100vh-10rem)] xl:max-h-[calc(100vh-14rem)] w-full md:w-[600px] rounded-none md:rounded-2xl md:border md:border-gray-200/60 md:shadow-[0_24px_48px_rgba(0,0,0,0.12),0_4px_12px_rgba(79,70,229,0.08)] overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ height: "100%" }}
          >
            {/* Sticky Header + Expandable Actions */}
            <div className="sticky top-0 z-10">
              {/* Green header bar */}
              <div
                className="flex items-center justify-between px-3 py-1.5 md:py-3 transition-all duration-200 shadow-md bg-gradient-to-r from-primary via-primary to-primary/90"
                style={{
                  backgroundColor:
                    vendorMetadata?.vendor?.primaryColor || undefined,
                  borderBottomColor:
                    vendorMetadata?.vendor?.accentColor || undefined,
                  borderBottomWidth: vendorMetadata?.vendor?.accentColor
                    ? "2px"
                    : undefined,
                }}
              >
                <div className="flex items-center gap-2">
                  {/* Back Arrow */}
                  <button
                    className={`min-w-[44px] min-h-[44px] flex items-center justify-center text-lg text-white rounded-lg transition-all duration-200 ${
                      !stepStack.length
                        ? "invisible"
                        : "visible hover:bg-white/20 hover:opacity-80 active:scale-95"
                    } cursor-pointer`}
                    onClick={handleBackClick}
                    aria-label="Go back"
                  >
                    <FontAwesomeIcon
                      icon={solid("arrow-left")}
                      className={i18n.dir() === "rtl" ? "rtl:rotate-180" : ""}
                    />
                  </button>
                  {/* Forward Arrow */}
                  <button
                    className={`min-w-[44px] min-h-[44px] flex items-center justify-center text-lg text-white rounded-lg transition-all duration-200 ${
                      !redoStack.length
                        ? "invisible"
                        : "visible hover:bg-white/20 hover:opacity-80 active:scale-95"
                    } cursor-pointer`}
                    onClick={handleForwardClick}
                    aria-label="Go forward"
                  >
                    <FontAwesomeIcon
                      icon={solid("arrow-right")}
                      className={i18n.dir() === "rtl" ? "rtl:rotate-180" : ""}
                    />
                  </button>
                </div>
                <h2 className="text-sm md:text-base font-semibold text-white text-center min-w-0 flex-1 px-1 leading-snug line-clamp-2">
                  {isLoading ? (
                    <Skeleton className="h-[20px] w-[250px] rounded-lg bg-gray-200 animate-pulse" />
                  ) : (
                    currentActivityData?.label?.[diagram.prescriptionLanguage]
                  )}
                </h2>
                <div className="flex items-center gap-2">
                  {/* Activity Notes Button */}
                  {currentActivity &&
                    diagram?.data?.[currentActivity]?.metadata
                      ?.additionalInfo && (
                      <ActivityNotesPopover
                        htmlContent={
                          diagram.data[currentActivity].metadata.additionalInfo
                        }
                        activityLabel={
                          currentActivityData?.label?.[
                            diagram.prescriptionLanguage
                          ] || currentActivity
                        }
                      />
                    )}
                  {/* Examples Button - desktop only (on mobile it moves to expandable row) */}
                  {!mobileActionsSlot && activityId && hasExamples && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-8 h-8 p-0 text-white hover:bg-white/20"
                      onClick={() => setShowExamplesManagement(true)}
                      title={
                        processId ? t("view_examples") : t("saved_examples")
                      }
                    >
                      <BookOpen className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Content language switcher — only shown when the activity
                      actually supports more than one language. Changes only
                      how this activity's content resolves, not the
                      platform/account UI language (i18n.language). */}
                  {Array.isArray(diagram.supportedLanguages) && diagram.supportedLanguages.length > 1 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="min-w-[36px] min-h-[36px] flex items-center justify-center text-white rounded-lg hover:bg-white/20 active:scale-95 transition-all duration-200"
                          title={t("content_language", "Content language")}
                        >
                          <Globe className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {diagram.supportedLanguages.map((code) => (
                          <DropdownMenuItem
                            key={code}
                            onClick={() => setContentLanguage(code)}
                          >
                            {t(LANGUAGES[code]?.label ?? code)}
                            {(contentLanguage || diagram.prescriptionLanguage || "en") === code && (
                              <span className="ms-auto text-xs text-muted-foreground">✓</span>
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {/* Decoration Button - desktop only (on mobile it moves to expandable row) */}
                  {!mobileActionsSlot && (
                    <DecorationButton
                      paneNotes={getNoteText(paneNotes[currentActivity])}
                      onPaneNotesChange={(value) => handleNotesChange(value)}
                      paneRecordings={getNoteRecordings(
                        paneNotes[currentActivity],
                      )}
                      onPaneRecordingsChange={handlePaneRecordingsChange}
                      prologue={prologue}
                      onPrologueChange={handlePrologueChange}
                      epilogue={epilogue}
                      onEpilogueChange={handleEpilogueChange}
                      isActivityAuthor={isActivityAuthor}
                      processId={processId}
                      initialIsExample={isMarkedAsExample}
                      showPublishOption={true}
                      currentPaneName={
                        currentActivityData?.label?.[
                          diagram.prescriptionLanguage
                        ] || "This Pane"
                      }
                      currentActivityId={currentActivity}
                      isReadOnly={false}
                      annotationDisplayMode={currentActivityData?.annotationDisplayMode || "on_demand"}
                      onAnnotationDisplayModeChange={handleAnnotationDisplayModeChange}
                    />
                  )}
                  {/* Chevron toggle for mobile actions row */}
                  {mobileActionsSlot && (
                    <button
                      onClick={() => setActionsExpanded((prev) => !prev)}
                      aria-expanded={actionsExpanded}
                      aria-controls="mobile-actions-row"
                      className="flex items-center justify-center min-w-[44px] min-h-[44px] text-white rounded-md hover:bg-white/20 active:scale-95 transition-transform"
                    >
                      <ChevronDown
                        className="w-5 h-5 transition-transform duration-200"
                        style={{
                          transform: actionsExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    </button>
                  )}
                  <div
                    className="text-lg text-white me-2"
                    onClick={handleCloseClick}
                  ></div>
                </div>
              </div>

              {/* Expandable actions row (mobile only) */}
              {mobileActionsSlot && (
                <div
                  id="mobile-actions-row"
                  className="overflow-hidden bg-primary/95"
                  style={{
                    maxHeight: actionsExpanded ? "3.5rem" : "0px",
                    transition: "max-height 0.25s ease",
                    backgroundColor:
                      vendorMetadata?.vendor?.primaryColor || undefined,
                  }}
                >
                  <div className="flex items-center justify-around px-4 py-1.5 border-t border-white/10">
                    {/* Examples Button - mobile (show only if examples exist) */}
                    {activityId && hasExamples && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-8 h-8 p-0 text-white hover:bg-white/20"
                        onClick={() => setShowExamplesManagement(true)}
                        title={
                          processId ? t("view_examples") : t("saved_examples")
                        }
                      >
                        <BookOpen className="w-4 h-4" />
                      </Button>
                    )}
                    {/* DecorationButton (rendered here because it needs RenderDialog internal state) */}
                    <DecorationButton
                      paneNotes={getNoteText(paneNotes[currentActivity])}
                      onPaneNotesChange={(value) => handleNotesChange(value)}
                      paneRecordings={getNoteRecordings(
                        paneNotes[currentActivity],
                      )}
                      onPaneRecordingsChange={handlePaneRecordingsChange}
                      prologue={prologue}
                      onPrologueChange={handlePrologueChange}
                      epilogue={epilogue}
                      onEpilogueChange={handleEpilogueChange}
                      isActivityAuthor={isActivityAuthor}
                      processId={processId}
                      initialIsExample={isMarkedAsExample}
                      showPublishOption={true}
                      currentPaneName={
                        currentActivityData?.label?.[
                          diagram.prescriptionLanguage
                        ] || "This Pane"
                      }
                      currentActivityId={currentActivity}
                      isReadOnly={false}
                      annotationDisplayMode={currentActivityData?.annotationDisplayMode || "on_demand"}
                      onAnnotationDisplayModeChange={handleAnnotationDisplayModeChange}
                    />
                    {/* Actions from parent (takeaways, chat, help, menu, lang) */}
                    {mobileActionsSlot}
                  </div>
                </div>
              )}
            </div>


            {/* Scrollable Content */}
            <div
              ref={contentRef}
              key={stepStack}
              dir={getDirForLanguage(contentLanguage || diagram.prescriptionLanguage)}
              className="flex-1 min-h-0 py-3 md:py-4 overflow-y-auto text-center duration-300 md:px-4 scroll-smooth animate-in fade-in slide-in-from-bottom-1 bg-gradient-to-b from-white via-white to-slate-50/60"
              onScroll={
                onContentScroll || mobileActionsSlot
                  ? (e) => {
                      const { scrollTop } = e.currentTarget;
                      const delta = scrollTop - lastScrollTop.current;
                      lastScrollTop.current = scrollTop;
                      if (Math.abs(delta) < 8) return;
                      // Auto-collapse expandable actions row on scroll down
                      if (delta > 0 && actionsExpanded) {
                        setActionsExpanded(false);
                      }
                      if (onContentScroll) {
                        onContentScroll(delta > 0 ? "down" : "up");
                      }
                    }
                  : undefined
              }
            >
              {isLoading ? (
                <div className="p-4">
                  <div className="flex flex-col space-y-3">
                    <Skeleton className="h-[125px] w-[90%] rounded-lg bg-gray-200 shimmer" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-[90%] shimmer" />
                      <Skeleton className="h-4 w-[80%] shimmer" />
                    </div>
                  </div>
                </div>
              ) : (
                <>{renderActivityComponents()}</>
              )}

              {/* Branding - at end of scrollable content */}
              {branding && (
                <div className="flex flex-row items-center justify-between px-3 py-2 mt-3 border-t md:px-4">
                  {/* Vendor Branding - Left Side */}
                  {vendorMetadata?.vendor && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] md:text-xs opacity-40">
                        {t("provided_by")}
                      </span>
                      {vendorMetadata.vendor._id ? (
                        <button
                          onClick={() =>
                            navigate(
                              `/marketplace/vendor/${vendorMetadata.vendor._id}`,
                            )
                          }
                          className="transition-all duration-200 hover:opacity-90 focus:outline-none active:scale-95"
                        >
                          {vendorMetadata.vendor.logoURL ? (
                            <img
                              className="h-auto w-[45px] md:w-[60px] opacity-70"
                              src={vendorMetadata.vendor.logoURL}
                              alt={vendorMetadata.vendor.displayName}
                              style={{ objectFit: "contain" }}
                            />
                          ) : (
                            <span className="text-sm font-semibold opacity-70">
                              {vendorMetadata.vendor.displayName}
                            </span>
                          )}
                        </button>
                      ) : (
                        <span className="text-sm opacity-70">
                          {vendorMetadata.vendor.displayName}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Powered by ClaryNext - Right Side */}
                  <div className="flex items-center">
                    <span className="text-[10px] md:text-xs opacity-40">
                      {t("powered_by") || "Powered by"}
                    </span>
                    <ClaryNextLogo
                        className="h-7 w-auto max-w-[90px] text-primary"
                        style={{ height: "1.75rem", width: "auto" }}
                        alt="Brand logo"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="relative shrink-0 bg-white" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}>
              <div className="absolute left-0 z-10 w-full h-2 pointer-events-none -top-2 bg-gradient-to-t from-gray-300/30 to-transparent" />
              <div
                ref={footerScrollRef}
                className="flex items-center gap-3 py-3 px-4 bg-white border-t border-gray-100 optionbuttons-container overflow-x-auto overscroll-x-contain scrollbar-thin scrollbar-thumb-rounded-full scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400"
                data-mobile-scroll
                data-overflows={footerScroll.canScroll ? "true" : "false"}
              >
                {currentActivityData?.options?.map((option, optIdx) => (
                  <Button
                    className="optionbuttons text-sm font-semibold min-h-[48px] px-8 rounded-xl shadow-md will-change-transform"
                    key={option.value}
                    onClick={() => handleOptionClick(optIdx)}
                    disabled={isSaving || savingOptionIdx !== null}
                    style={
                      option.buttonColor
                        ? {
                            backgroundColor: option.buttonColor,
                            color: "white",
                          }
                        : vendorMetadata?.vendor?.primaryColor
                          ? {
                              backgroundColor:
                                vendorMetadata.vendor.primaryColor,
                              color: "white",
                            }
                          : undefined
                    }
                  >
                    {isSaving || savingOptionIdx === optIdx ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      option.text[diagram.prescriptionLanguage]
                    )}
                  </Button>
                ))}
              </div>
              {/* Scroll indicator — visible only when options overflow */}
              <div
                style={{ display: footerScroll.canScroll ? "" : "none" }}
              >
                <div
                  ref={scrollbarTrackRef}
                  className="mx-auto mt-1.5 h-1 w-12 rounded-full bg-gray-200 overflow-hidden cursor-pointer"
                  onPointerDown={handleScrollbarPointerDown}
                >
                  <div className="h-full rounded-full bg-gray-400" />
                </div>
              </div>
            </div>

          </div>
        </div>
      ) : (
        <></>
      )}

      {/* Save Example Dialog */}
      <SaveExampleDialog
        open={showSaveExampleDialog}
        onOpenChange={setShowSaveExampleDialog}
        onSave={handleSaveExample}
        onDiscard={handleDiscardExample}
        activityName={
          currentActivityData?.label?.[diagram.prescriptionLanguage]
        }
        isActivityAuthor={isActivityAuthor}
      />

      {/* Submit Example Consent Dialog */}
      <SubmitExampleConsentDialog
        open={showSubmitConsentDialog}
        onOpenChange={setShowSubmitConsentDialog}
        onSubmit={handleSubmitExampleConfirm}
        userName={currentUser?.displayName || currentUser?.email || "You"}
      />

      {/* Examples Management */}
      <ExampleManagement
        open={showExamplesManagement}
        onOpenChange={setShowExamplesManagement}
        activityId={activityId}
        onLoadExample={handleLoadExample}
        onFollowExample={onFollowExample}
        mode={processId ? "process" : "preview"}
      />

      {/* User Recording Dialog */}
      <RecordingDialog
        open={userRecordingDialogOpen}
        onOpenChange={setUserRecordingDialogOpen}
        type={userRecordingDialogType}
        onComplete={handleUserRecordingComplete}
        context="pane"
        processId={processId}
        activityId={userRecordingTargetKey}
      />

      {/* User Image Upload Dialog */}
      <ImageUploadDialog
        open={userImageUploadDialogOpen}
        onOpenChange={setUserImageUploadDialogOpen}
        onComplete={handleUserImageUploadComplete}
        context="pane"
        processId={processId}
        activityId={userImageTargetKey}
      />
    </>
  );
};

export default RenderDialog;
